import { useEffect, useRef, useState } from "react";
import { getAppointmentsByPhone } from "../APIs/api-routes.util";
import { AppointmentRow, PhoneHistoryResponse } from "../APIs/api.types";
import { bookingTypeLabel, formatTokenLabel } from "../common/booking.util";
import { Badge } from "../components/ui/Badge";
import { EmptyState } from "../components/ui/PagePrimitives";
import { apiSubscribe } from "../lib/api-subscribe";

function GetPhoneBookings({
  phone,
  onSelect,
  onLoading,
}: {
  phone: string;
  onSelect: (items: { name: string; guardianName: string }) => void;
  onLoading: (v: boolean) => void;
}) {
  const [records, setRecords] = useState<AppointmentRow[]>([]);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const onLoadingRef = useRef(onLoading);
  onLoadingRef.current = onLoading;

  useEffect(() => {
    if (!phone) {
      setRecords([]);
      setLoaded(true);
      return;
    }
    setError("");
    setLoaded(false);
    onLoadingRef.current(true);
    return apiSubscribe<PhoneHistoryResponse>(
      getAppointmentsByPhone(phone),
      {
        next: (body) => {
          onLoadingRef.current(false);
          setLoaded(true);
          setRecords(body.data || []);
        },
        error: () => {
          onLoadingRef.current(false);
          setLoaded(true);
          setError("Could not load appointment history.");
          setRecords([]);
        },
      }
    );
  }, [phone]);

  if (error) {
    return <EmptyState title="Could not load history" description={error} />;
  }

  if (loaded && records.length === 0) {
    return (
      <EmptyState
        title="No previous appointments"
        description="No records found for this phone number."
      />
    );
  }

  if (!loaded) {
    return null;
  }

  return (
    <ul className="divide-y divide-brand-100 overflow-hidden rounded-xl border border-brand-100">
      {records.map((item) => {
        const urgent = item.booking_type === "urgent";
        return (
          <li key={item.ID}>
            <button
              type="button"
              onClick={() =>
                onSelect({ name: item.Name, guardianName: item.GuardianName })
              }
              className={`flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-brand-50 ${
                urgent ? "bg-status-danger-bg/30" : "bg-brand-50/20"
              }`}
            >
              <span
                className={`shrink-0 rounded-md px-1.5 py-0.5 text-xs font-bold ${
                  urgent
                    ? "bg-accent-rose-light text-accent-rose-dark"
                    : "bg-brand-100 text-brand-800"
                }`}
              >
                {formatTokenLabel(item.token_number, item.booking_type)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-content-primary">{item.Name}</p>
                <p className="truncate text-2xs text-content-muted">
                  {item.Date} · {item.GuardianName}
                </p>
              </div>
              <Badge variant={urgent ? "urgent" : "normal"} className="shrink-0">
                {bookingTypeLabel(item.booking_type)}
              </Badge>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export default GetPhoneBookings;
